import StatCards from './ui/StatCards';
import ModalLayer from './ModalLayer';
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
} from 'react';
import './Departments.css';

interface Faculty {
  id: number;
  code: string;
  name: string;
  description: string;
}

interface Department {
  id: number;
  departmentCode: string;
  name: string;
  description: string;
  facultyId: number;
}

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  modalClassName?: string;
}

interface DepartmentsProps {
  userRole: string;
  userDepartmentCode?: string;
}

/* -------------------------------
   Small Utilities
-------------------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const toCSV = (rows: any[]) => {
  if (!rows.length) return '';
  const cols = Object.keys(rows[0]);
  const escape = (v: any) => {
    const s = v === null || v === undefined ? '' : String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  return [cols.join(','), ...rows.map((r) => cols.map((c) => escape(r[c])).join(','))].join('\n');
};

const downloadBlob = (content: string, name: string, type: string) => {
  const a = document.createElement('a');
  const file = new Blob([content], { type });
  a.href = URL.createObjectURL(file);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

/* -------------------------------
   Reusable Modal
-------------------------------- */
const Modal: React.FC<ModalProps> = ({ visible, onClose, children, modalClassName }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e as any).key === 'Escape') onClose();
    };
    const trapFocus = (e: any) => {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target)) {
        panelRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKey as any);
    document.addEventListener('focus', trapFocus, true);
    return () => {
      document.removeEventListener('keydown', onKey as any);
      document.removeEventListener('focus', trapFocus, true);
    };
  }, [visible, onClose]);

  if (!visible) return null;
  return (
    <ModalLayer className="fac-modal-overlay" onClick={onClose} aria-modal="true" role="dialog">
      <div
        className={`fac-modal ${modalClassName || ''}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
        ref={panelRef}
      >
        <button className="fac-modal-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        {children}
      </div>
    </ModalLayer>
  );
};

/* -------------------------------
   Main Component
-------------------------------- */
const Departments: React.FC<DepartmentsProps> = ({ userRole, userDepartmentCode }) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [error, setError] = useState('');

  // Modals
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);

  // Modal messages/actions
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});

  // Selected department for view/edit
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);

  // Edit form
  const [editForm, setEditForm] = useState<{
    departmentCode: string;
    name: string;
    description: string;
    facultyId: number | '';
  } | null>(null);

  // Add form
  const initialAddForm = {
    departmentCode: '',
    name: '',
    description: '',
    facultyId: '' as number | '',
  };
  const [addForm, setAddForm] = useState<typeof initialAddForm>(initialAddForm);

  // Search / paging / sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<keyof Department | 'facultyName'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const searchRef = useRef<HTMLInputElement>(null);

  /* -------------------------------
     Fetch data
  -------------------------------- */
  const fetchFaculties = async () => {
    try {
      const res = await fetch('/v1/api/systems-settings/faculties/get-all');
      const data = await res.json();
      if (Array.isArray(data)) setFaculties(data);
    } catch {
      setError('Error fetching faculties');
    }
  };

  const fetchDepartments = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/v1/api/departments/all');
      const data = await res.json();
      if (Array.isArray(data)) {
        setDepartments(data);
      } else {
        setError('Unexpected departments response');
      }
    } catch {
      setError('Error fetching departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaculties();
    fetchDepartments();
  }, []);

  // debounce searchTerm
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchTerm.trim()), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* -------------------------------
     Role filtering
  -------------------------------- */
  const roleDepartments = useMemo(() => {
    if (['FACULTY_RAP', 'FACULTY_ADMIN'].includes(userRole) && userDepartmentCode) {
      return departments.filter(
        (dep) => dep.departmentCode.toLowerCase() === userDepartmentCode.toLowerCase()
      );
    }
    return departments;
  }, [departments, userRole, userDepartmentCode]);

  /* -------------------------------
     Derived helpers
  -------------------------------- */
  const getFacultyName = (facultyId: number) =>
    faculties.find((f) => f.id === facultyId)?.name || '';

  const withFacultyName = useMemo(
    () =>
      roleDepartments.map((d) => ({
        ...d,
        facultyName: getFacultyName(d.facultyId),
      })),
    [roleDepartments, faculties]
  );

  // filter + sort
  const filtered = useMemo(() => {
    const q = debounced.toLowerCase();
    let rows = !q
      ? withFacultyName
      : withFacultyName.filter(
          (dep) =>
            dep.name.toLowerCase().includes(q) ||
            dep.departmentCode.toLowerCase().includes(q) ||
            dep.description.toLowerCase().includes(q) ||
            dep.facultyName.toLowerCase().includes(q)
        );

    rows = rows.sort((a: any, b: any) => {
      const A = (a[sortBy] ?? '').toString().toLowerCase();
      const B = (b[sortBy] ?? '').toString().toLowerCase();
      if (A < B) return sortDir === 'asc' ? -1 : 1;
      if (A > B) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [withFacultyName, debounced, sortBy, sortDir]);

  // paging
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paginated = useMemo(
    () => filtered.slice((page - 1) * perPage, page * perPage),
    [filtered, page, perPage]
  );

  /* -------------------------------
     Keyboard shortcuts
     - "/" focus search
     - "n" new department
     - "Esc" clears search / closes modal (handled in Modal)
  -------------------------------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tag = target?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || target?.isContentEditable;

      if (e.key === '/' && !isTyping) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if ((e.key === 'n' || e.key === 'N') && !isTyping) {
        setAddModalVisible(true);
      }
    };
    window.addEventListener('keydown', onKey as any);
    return () => window.removeEventListener('keydown', onKey as any);
  }, []);

  /* -------------------------------
     Alerts / confirms
  -------------------------------- */
  const showAlert = (msg: string) => {
    setAlertMessage(msg);
    setAlertModalVisible(true);
  };

  /* -------------------------------
     Actions
  -------------------------------- */
  const handleViewDepartment = (dep: Department) => {
    setSelectedDepartment(dep);
    setViewModalVisible(true);
  };

  const handleEditDepartment = (dep: Department) => {
    setSelectedDepartment(dep);
    setEditForm({
      departmentCode: dep.departmentCode,
      name: dep.name,
      description: dep.description,
      facultyId: dep.facultyId,
    });
    setEditModalVisible(true);
  };

  const handleDeleteDepartment = (id: number) => {
    setConfirmMessage('Are you sure you want to delete this department?');
    setConfirmAction(() => async () => {
      try {
        const response = await fetch(`/v1/api/departments/delete/${id}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          showAlert('Department deleted successfully');
          setLoadingTable(true);
          await sleep(250);
          await fetchDepartments();
        } else {
          showAlert('Error deleting department');
        }
      } catch {
        showAlert('Error deleting department');
      }
      setConfirmModalVisible(false);
      setLoadingTable(false);
    });
    setConfirmModalVisible(true);
  };

  const handleEditFormChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    if (!editForm) return;
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: name === 'facultyId' ? Number(value) : value });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedDepartment || !editForm) return;
    const payload = {
      departmentCode: editForm.departmentCode,
      name: editForm.name,
      description: editForm.description,
    };
    try {
      const response = await fetch(
        `/v1/api/departments/update/${selectedDepartment.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        showAlert('Department updated successfully');
        setEditModalVisible(false);
        setLoadingTable(true);
        await sleep(250);
        await fetchDepartments();
      } else {
        showAlert('Error updating department');
      }
    } catch {
      showAlert('Error updating department');
    } finally {
      setLoadingTable(false);
    }
  };

  const handleAddFormChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: name === 'facultyId' ? Number(value) : value }));
  };

  const handleAddSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!addForm.facultyId) {
      showAlert('Please select a faculty');
      return;
    }
    const payload = {
      departmentCode: addForm.departmentCode,
      name: addForm.name,
      description: addForm.description,
    };
    try {
      const response = await fetch(
        `/v1/api/departments/create/${addForm.facultyId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        showAlert('Department added successfully');
        setAddModalVisible(false);
        setAddForm(initialAddForm);
        setLoadingTable(true);
        await sleep(250);
        await fetchDepartments();
      } else {
        showAlert('Error adding department');
      }
    } catch {
      showAlert('Error adding department');
    } finally {
      setLoadingTable(false);
    }
  };

  const handleSort = (col: keyof Department | 'facultyName') => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const handleExportCSV = () => {
    const csv = toCSV(filtered);
    if (!csv) {
      showAlert('Nothing to export');
      return;
    }
    downloadBlob(csv, 'departments.csv', 'text/csv;charset=utf-8;');
  };

  const caret = (col: keyof Department | 'facultyName') => {
    if (sortBy !== col) return <span className="sort-caret">↕</span>;
    return <span className={`sort-caret active`}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  /* -------------------------------
     Render
  -------------------------------- */
  return (
    <div className="fac-container">
      {/* Header */}
      <div className="fac-header">
        <div className="fac-title-wrap">
          <h2 className="fac-title">Departments/Centres</h2>
          <p className="fac-subtitle">Create, edit and manage departments across faculties.</p>
        </div>
        <div className="fac-actions">
          <button className="fac-secondary-btn" onClick={handleExportCSV} aria-label="Export CSV">
            Export CSV
          </button>
          <button
            className="fac-add-btn"
            onClick={() => setAddModalVisible(true)}
            aria-label="Add Department (N)"
            title="Shortcut: N"
          >
            Add Department
          </button>
        </div>
      </div>

      <StatCards items={[{label:'Departments / centres',value:departments.length,total:departments.length,meterLabel:'Accessible units'},{label:'Matching filters',value:filtered.length,total:departments.length,meterLabel:'Current search results'}]}/>

      {/* Filters */}
      <div className="fac-filter-bar" role="region" aria-label="Filters">
        <div className="fac-filters-left">
          <label className="fac-perpage">
            Show{' '}
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              aria-label="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>{' '}
            per page
          </label>
        </div>

        <div className="fac-filters-right">
          <div className="fac-search-wrap">
            <input
              ref={searchRef}
              className="fac-search"
              type="search"
              placeholder="Search name, code, faculty, description"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              aria-label="Search"
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Escape') setSearchTerm('');
              }}
              autoComplete="off"
            />
            <span className="fac-search-hint">Press “/” to search</span>
          </div>
        </div>
      </div>

      {/* Table / Empty / Loading */}
      {loading ? (
        <div className="fac-loading">
          <div className="sk-row" />
          <div className="sk-row" />
          <div className="sk-row" />
        </div>
      ) : error ? (
        <p className="fac-error">{error}</p>
      ) : (
        <>
          <div className="fac-table-wrap" tabIndex={0}>
            <table className="fac-table">
              <thead>
                <tr>
                  <th className="th-id th-sort" onClick={() => handleSort('id')} role="button" tabIndex={0}>
                    ID {caret('id')}
                  </th>
                  <th className="th-code th-sort" onClick={() => handleSort('departmentCode')} role="button" tabIndex={0}>
                    Dept Code {caret('departmentCode')}
                  </th>
                  <th className="th-name th-sort" onClick={() => handleSort('name')} role="button" tabIndex={0}>
                    Dept Name {caret('name')}
                  </th>
                  <th className="th-desc">Dept Description</th>
                  <th className="th-fac th-sort" onClick={() => handleSort('facultyName')} role="button" tabIndex={0}>
                    Faculty {caret('facultyName')}
                  </th>
                  <th className="fac-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingTable ? (
                  <>
                    {Array.from({ length: Math.min(perPage, 8) }).map((_, i) => (
                      <tr key={`sk-${i}`} className="row-skeleton">
                        <td colSpan={6}>
                          <div className="sk-row" />
                        </td>
                      </tr>
                    ))}
                  </>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2.2rem' }}>
                      <div className="fac-empty">
                        <div className="fac-empty-illustration" aria-hidden="true">📄✨</div>
                        <h4>No departments found</h4>
                        <p>Try adjusting your search or add a new department.</p>
                        <button className="fac-add-btn" onClick={() => setAddModalVisible(true)}>
                          Add Department
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((dep) => (
                    <tr key={dep.id}>
                      <td>{dep.id}</td>
                      <td>
                        <span className="fac-code">{dep.departmentCode}</span>
                      </td>
                      <td className="cell-title">
                        <div className="title-wrap">
                          <span className="title-text">{dep.name}</span>
                        </div>
                      </td>
                      <td className="cell-desc">
                        <span className="desc-clamp">{dep.description}</span>
                      </td>
                      <td>{getFacultyName(dep.facultyId)}</td>
                      <td className="cell-actions">
                        <button
                          onClick={() => handleViewDepartment(dep)}
                          className="fac-action-btn"
                          aria-label={`View ${dep.name}`}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEditDepartment(dep)}
                          className="fac-action-btn"
                          aria-label={`Edit ${dep.name}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDepartment(dep.id)}
                          className="fac-action-btn fac-delete-btn"
                          aria-label={`Delete ${dep.name}`}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="fac-pagination-controls" role="navigation" aria-label="Pagination">
            <div className="pg-left">
              <span className="fac-results-count">
                {filtered.length} result{filtered.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="pg-right">
              <button
                className="fac-pagination-btn"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                >
                  Prev
                </button>

                <span className="pg-index">
                  Page {page} / {totalPages}
                </span>

                <button
                  className="fac-pagination-btn"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  aria-label="Next page"
                >
                  Next
                </button>

            </div>
          </div>
        </>
      )}

      {/* View Modal */}
      <Modal visible={viewModalVisible} onClose={() => setViewModalVisible(false)}>
        {selectedDepartment && (
          <div className="fac-modal-content">
            <h3 className="fac-modal-title">Department Details</h3>
            <div className="fac-kv">
              <div><strong>ID</strong><span>{selectedDepartment.id}</span></div>
              <div><strong>Department Code</strong><span>{selectedDepartment.departmentCode}</span></div>
              <div><strong>Name</strong><span>{selectedDepartment.name}</span></div>
              <div><strong>Description</strong><span>{selectedDepartment.description}</span></div>
              <div><strong>Faculty</strong><span>{getFacultyName(selectedDepartment.facultyId)}</span></div>
            </div>
            <div className="fac-modal-actions">
              <button className="fac-secondary-btn" onClick={() => setViewModalVisible(false)}>Close</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal visible={editModalVisible} onClose={() => setEditModalVisible(false)} modalClassName="fac-large-modal">
        {editForm && (
          <form className="fac-modal-form" onSubmit={handleSaveEdit}>
            <h3 className="fac-modal-title">Edit Department</h3>
            <div className="fac-form-grid">
              <div className="fac-form-group">
                <label className="modal-label">Select Faculty</label>
                <select aria-label="faculty Id"
                  name="facultyId"
                  value={editForm.facultyId}
                  onChange={handleEditFormChange}
                  required
                >
                  <option value="">--Select Faculty--</option>
                  {faculties.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fac-form-group">
                <label className="modal-label">Department Name</label>
                <input aria-label="name"
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                  required
                />
              </div>

              <div className="fac-form-group">
                <label className="modal-label">Department Code</label>
                <input aria-label="department Code"
                  type="text"
                  name="departmentCode"
                  value={editForm.departmentCode}
                  onChange={handleEditFormChange}
                  required
                />
              </div>

              <div className="fac-form-group fac-form-span2">
                <label className="modal-label">Department Description</label>
                <textarea aria-label="description"
                  name="description"
                  value={editForm.description}
                  onChange={handleEditFormChange}
                  required
                  className="fac-big-textarea"
                  rows={5}
                />
              </div>
            </div>

            <div className="fac-modal-actions">
              <button type="button" className="fac-secondary-btn" onClick={() => setEditModalVisible(false)}>
                Cancel
              </button>
              <button type="submit" className="fac-modal-submit">Save</button>
            </div>
          </form>
        )}
      </Modal>

      {/* Add Modal */}
      <Modal visible={addModalVisible} onClose={() => setAddModalVisible(false)} modalClassName="fac-large-modal">
        <form className="fac-modal-form" onSubmit={handleAddSubmit}>
          <h3 className="fac-modal-title">Register New Department</h3>
          <hr className="fac-modal-separator" />
          <div className="fac-form-grid">
            <div className="fac-form-group">
              <label>Select Faculty</label>
              <select aria-label="faculty Id" name="facultyId" value={addForm.facultyId} onChange={handleAddFormChange} required>
                <option value="">--Select Faculty--</option>
                {faculties.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="fac-form-group">
              <label>Department Name</label>
              <input aria-label="name" type="text" name="name" value={addForm.name} onChange={handleAddFormChange} required />
            </div>

            <div className="fac-form-group">
              <label>Department Code</label>
              <input aria-label="department Code"
                type="text"
                name="departmentCode"
                value={addForm.departmentCode}
                onChange={handleAddFormChange}
                required
              />
            </div>

            <div className="fac-form-group fac-form-span2">
              <label>Department Description</label>
              <textarea aria-label="description"
                name="description"
                value={addForm.description}
                onChange={handleAddFormChange}
                required
                className="fac-big-textarea"
                rows={5}
              />
            </div>
          </div>

          <div className="fac-modal-actions">
            <button type="button" className="fac-secondary-btn" onClick={() => setAddModalVisible(false)}>
              Cancel
            </button>
            <button type="submit" className="fac-modal-submit">Register</button>
          </div>
        </form>
      </Modal>

      {/* Confirmation Modal */}
      <Modal visible={confirmModalVisible} onClose={() => setConfirmModalVisible(false)}>
        <div className="fac-confirm-content">
          <p>{confirmMessage}</p>
          <div className="fac-confirm-actions">
            <button className="fac-confirm-btn" onClick={() => confirmAction()}>
              Yes
            </button>
            <button className="fac-confirm-btn fac-confirm-cancel" onClick={() => setConfirmModalVisible(false)}>
              No
            </button>
          </div>
        </div>
      </Modal>

      {/* Alert Modal */}
      <Modal visible={alertModalVisible} onClose={() => setAlertModalVisible(false)}>
        <div className="fac-alert-content">
          <p>{alertMessage}</p>
          <button className="fac-alert-btn" onClick={() => setAlertModalVisible(false)}>
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Departments;
